import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight, Sparkles, ShieldCheck, Zap } from "lucide-react";
import { publicPlansApi } from "../lib/api";
import { SEO } from "../components/seo/SEO";
import { getPageSeo } from "../lib/seoConfig";
import { breadcrumbSchema, faqSchema } from "../components/seo/schema";
import { FaqAccordion } from "../components/marketing/FaqAccordion";

const money = (n) => `$${Number(n || 0).toLocaleString()}`;

export default function Pricing() {
  const [plans, setPlans] = useState([]);
  const [term, setTerm] = useState("monthly");

  useEffect(() => {
    publicPlansApi.list().then(setPlans).catch(() => {});
  }, []);

  const popularName = "Pro";
  const seo = getPageSeo("/pricing");
  const pricingFaqs = [
    { question: "Does ServiceGuru Pro have a free trial?", answer: "Yes — every plan starts with a 14-day free trial and no credit card is required to sign up." },
    { question: "Can I change plans later?", answer: "Yes, you can upgrade or downgrade your plan and add or remove seats at any time from Company Settings." },
    { question: "Is pricing per user or flat rate?", answer: "ServiceGuru Pro is priced per seat, so you only pay for the technicians, dispatchers, and office staff who actually use the platform." },
  ];

  return (
    <div className="min-h-screen bg-[#0B1220] text-slate-200">
      <SEO
        title={seo.title}
        description={seo.description}
        path="/pricing"
        jsonLd={[breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Pricing", path: "/pricing" }]), faqSchema(pricingFaqs)]}
      />
      {/* Nav */}
      <header className="sticky top-0 z-20 border-b border-white/10 bg-[#0B1220]/85 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <Link to="/" className="flex items-center gap-2.5" data-testid="pricing-logo">
              <div className="h-9 w-9 rounded-lg bg-white flex items-center justify-center overflow-hidden">
                <img src="/brand-logo.png" alt="ServiceGuru Pro" className="h-full w-full object-contain" />
              </div>
              <span className="font-bold text-white text-[15px]">ServiceGuru Pro</span>
            </Link>
            <nav className="hidden lg:flex items-center gap-7 text-[13.5px] font-medium text-slate-300">
              <Link to="/features" data-testid="pricing-nav-features" className="hover:text-white transition-colors">Features</Link>
              <Link to="/industries" data-testid="pricing-nav-industries" className="hover:text-white transition-colors">Industries</Link>
              <Link to="/solutions" data-testid="pricing-nav-solutions" className="hover:text-white transition-colors">Solutions</Link>
              <Link to="/resources" data-testid="pricing-nav-resources" className="hover:text-white transition-colors">Resources</Link>
              <Link to="/pricing" data-testid="pricing-nav-pricing" className="hover:text-white transition-colors">Pricing</Link>
              <Link to="/contact" data-testid="pricing-nav-contact" className="hover:text-white transition-colors">Contact</Link>
            </nav>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/login" data-testid="pricing-signin" className="px-3.5 py-2 rounded-lg text-[13.5px] text-slate-300 hover:text-white">Sign in</Link>
            <Link to="/signup" data-testid="pricing-nav-cta" className="px-3.5 py-2 rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-[13.5px] font-semibold">Start free trial</Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute top-[-20%] left-1/2 -translate-x-1/2 h-80 w-[44rem] bg-blue-500/10 blur-[140px] rounded-full pointer-events-none" />
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
          backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "34px 34px",
        }} />
        <div className="relative max-w-3xl mx-auto px-6 pt-20 pb-12 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[12px] font-semibold mb-6">
            <Sparkles className="h-3.5 w-3.5" /> 14-day free trial · No credit card required
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] tracking-tight mb-5">
            Run your field service<br />business on autopilot.
          </h1>
          <p className="text-[15px] text-slate-400 leading-relaxed max-w-xl mx-auto mb-8">
            Dispatch, CRM, estimates, invoicing, payroll and AI diagnostics — one workspace for your whole crew. Pay only for the seats you use.
          </p>
          <div className="flex items-center justify-center gap-3">
            <Link to="/signup" data-testid="pricing-hero-cta" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-[15px] font-semibold shadow-lg shadow-blue-900/30 transition-colors">
              Start free trial <ArrowRight className="h-4.5 w-4.5" />
            </Link>
            <a href="#plans" className="px-6 py-3 rounded-xl border border-white/15 text-slate-200 text-[15px] font-medium hover:bg-white/5">See plans</a>
          </div>
          <div className="flex items-center justify-center gap-6 mt-8 text-[12.5px] text-slate-500">
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-emerald-400" /> Cancel anytime</span>
            <span className="inline-flex items-center gap-1.5"><Zap className="h-4 w-4 text-amber-400" /> Set up in minutes</span>
          </div>
        </div>
      </section>

      {/* Plans */}
      <section id="plans" className="max-w-6xl mx-auto px-6 pb-24">
        <div className="flex items-center justify-center mb-10">
          <div className="inline-flex rounded-xl bg-white/5 border border-white/10 p-1 text-[13px] font-semibold">
            {["monthly", "annual"].map((t) => (
              <button
                key={t} data-testid={`pricing-term-${t}`} onClick={() => setTerm(t)}
                className={`px-4 py-1.5 rounded-lg capitalize transition-colors ${term === t ? "bg-[#2563EB] text-white" : "text-slate-400 hover:text-slate-200"}`}
              >
                {t}{t === "annual" && <span className="ml-1.5 text-emerald-300">2 months free</span>}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {plans.map((p) => {
            const price = term === "annual" ? p.perSeatAnnual : p.perSeatMonthly;
            const popular = p.name === popularName;
            return (
              <div
                key={p.id}
                data-testid={`pricing-card-${p.id}`}
                className={`relative rounded-2xl border p-7 flex flex-col ${popular ? "border-blue-500/50 bg-gradient-to-b from-blue-500/[0.08] to-transparent ring-1 ring-blue-500/30" : "border-white/10 bg-white/[0.02]"}`}
              >
                {popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-[#2563EB] text-white text-[11px] font-bold tracking-wide">MOST POPULAR</span>
                )}
                <div className="text-[17px] font-bold text-white mb-1">{p.name}</div>
                <div className="text-[12.5px] text-slate-500 mb-5 min-h-[34px]">
                  {p.minSeats > 1 ? `Minimum ${p.minSeats} seats` : "Great for small teams"}
                </div>
                <div className="flex items-baseline gap-1.5 mb-6">
                  <span className="text-4xl font-bold text-white">{money(price)}</span>
                  <span className="text-[13px] text-slate-500">/seat/{term === "annual" ? "yr" : "mo"}</span>
                </div>
                <Link
                  to={`/signup?plan=${p.id}&term=${term}`}
                  data-testid={`pricing-cta-${p.id}`}
                  className={`mb-6 inline-flex items-center justify-center gap-2 h-11 rounded-xl text-[14px] font-semibold transition-colors ${popular ? "bg-[#2563EB] hover:bg-[#1D4ED8] text-white" : "border border-white/15 text-white hover:bg-white/5"}`}
                >
                  Start free trial <ArrowRight className="h-4 w-4" />
                </Link>
                <ul className="space-y-2.5">
                  {(p.features || []).map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-[13px] text-slate-300">
                      <Check className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        <p className="text-center text-[12.5px] text-slate-500 mt-10">
          All plans include a 14-day free trial. Per-seat billing — you only pay for registered users.
        </p>
      </section>

      <section className="max-w-3xl mx-auto px-5 lg:px-8 pb-20">
        <h2 className="text-2xl font-bold text-white text-center mb-8">Frequently asked questions</h2>
        <FaqAccordion faqs={pricingFaqs} />
        <div className="mt-10 flex flex-wrap items-center justify-center gap-4 text-[13.5px]">
          <Link to="/features" data-testid="pricing-see-features-link" className="text-blue-400 hover:text-blue-300 transition-colors">
            Explore all features →
          </Link>
          <Link to="/resources" data-testid="pricing-see-resources-link" className="text-slate-400 hover:text-white transition-colors">
            Read our resources →
          </Link>
        </div>
      </section>

      <footer className="border-t border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-8 flex items-center justify-between flex-wrap gap-3 text-[12px] text-slate-500">
          <span>© 2026 ServiceGuru Pro</span>
          <div className="flex items-center gap-4">
            <Link to="/login" className="hover:text-slate-300">Sign in</Link>
            <Link to="/signup" className="hover:text-slate-300">Start free trial</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
